import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Box,
  Button,
  HStack,
  VStack
} from '@chakra-ui/react'
import { FC, useMemo, useState } from 'react'

import { formatLotes } from '@helper/mrp/lotes'

import { IPendingOrderItem } from '@base/@types/providers/Mrp/ApprovalProvider'

interface GroupTableProps {
  items: IPendingOrderItem[]
}

// Colunas numéricas/curtas: cabeçalho e valor centralizados, sem quebra
const centerCol = { textAlign: 'center', whiteSpace: 'nowrap' } as const

// Coluna Venda Média Diária: habilitar após confirmação do time de negócio
const SHOW_VENDA_MEDIA_DIARIA = false

const formatNumber = (
  value?: number | null,
  options?: Intl.NumberFormatOptions
) => (value == null ? '-' : value.toLocaleString('pt-BR', options))

const formatDesconto = (desconto?: number | null) =>
  desconto && desconto > 0
    ? `${desconto.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
    : '-'

const GroupTable: FC<GroupTableProps> = ({ items }) => {
  // Começa em "só alterados" apenas quando há itens alterados (senão abriria vazio)
  const [showOnlyAltered, setShowOnlyAltered] = useState(() =>
    items.some(item => item.quantidadeDesejadaAlterada)
  )

  const getAlteracaoColor = (percentual: number) => {
    if (percentual === 0) return 'black'
    return percentual > 0 ? '#82CF69' : '#DE0B0B'
  }

  // Filtrar e ordenar itens
  const filteredAndSortedItems = useMemo(() => {
    let filteredItems = [...items]

    // Aplicar filtro
    if (showOnlyAltered) {
      filteredItems = filteredItems.filter(
        item => item.quantidadeDesejadaAlterada
      )
    } else {
      // Quando mostrar todos, ordenar alterados primeiro
      filteredItems = filteredItems.sort((a, b) => {
        if (a.quantidadeDesejadaAlterada && !b.quantidadeDesejadaAlterada) {
          return -1 // a vem antes de b
        }
        if (!a.quantidadeDesejadaAlterada && b.quantidadeDesejadaAlterada) {
          return 1 // b vem antes de a
        }
        return 0
      })
    }

    return filteredItems
  }, [items, showOnlyAltered])

  // Calcular contadores
  const itemCounts = useMemo(() => {
    const altered = items.filter(item => item.quantidadeDesejadaAlterada).length
    const normal = items.length - altered
    return { total: items.length, altered, normal }
  }, [items])

  return (
    <Box mt={4}>
      {/* Botão de Filtro Toggle */}
      <HStack spacing={3} mb={4}>
        <Button
          size="sm"
          colorScheme={showOnlyAltered ? 'purple' : 'blue'}
          variant="solid"
          onClick={() => setShowOnlyAltered(!showOnlyAltered)}
        >
          {showOnlyAltered
            ? `Ver Tudo (${itemCounts.total})`
            : `Ver Apenas Alterados (${itemCounts.altered})`}
        </Button>
      </HStack>

      <Box
        overflowX="auto"
        overflowY="auto"
        maxH="400px"
        bg="white"
        borderRadius="md"
      >
        <Table size="sm" variant="simple" bg="white">
          <Thead bg="gray.50">
            <Tr>
              <Th {...centerCol}>Loja</Th>
              <Th {...centerCol}>Código</Th>
              <Th whiteSpace="nowrap">Material</Th>
              <Th {...centerCol}>Curva</Th>
              <Th {...centerCol}>Estoque Disp.</Th>
              {SHOW_VENDA_MEDIA_DIARIA && <Th {...centerCol}>VMD</Th>}
              <Th {...centerCol}>Cobertura (dias)</Th>
              <Th {...centerCol}>Qtd. Recomendada</Th>
              <Th {...centerCol}>Qtd. Desejada</Th>
              <Th {...centerCol}>% Alteração / Lotes</Th>
              <Th {...centerCol}>Tipo Pedido</Th>
              <Th {...centerCol}>Desconto</Th>
              <Th whiteSpace="nowrap">Justificativa</Th>
            </Tr>
          </Thead>
          <Tbody bg="white">
            {filteredAndSortedItems.map((item, index) => {
              return (
                <Tr
                  key={item.id}
                  bg={
                    item.quantidadeDesejadaAlterada
                      ? 'purple.50' // Background lilás para itens alterados
                      : index % 2 === 0
                      ? 'white'
                      : 'gray.50'
                  }
                  // Borda em todas as linhas (transparente nas normais) para não
                  // deslocar as células das linhas alteradas
                  borderLeft="4px solid"
                  borderLeftColor={
                    item.quantidadeDesejadaAlterada
                      ? 'purple.500'
                      : 'transparent'
                  }
                  _hover={{
                    bg: item.quantidadeDesejadaAlterada
                      ? 'purple.100'
                      : 'gray.100'
                  }}
                >
                  <Td {...centerCol}>{item.codigoLoja}</Td>
                  <Td {...centerCol}>{item.codigoMaterial}</Td>
                  <Td>
                    <Text noOfLines={2} minW="160px" maxW="240px">
                      {item.nomeMaterial}
                    </Text>
                  </Td>
                  <Td {...centerCol}>{item.curvaABC || '-'}</Td>
                  <Td {...centerCol}>{formatNumber(item.estoqueDisponivel)}</Td>
                  {SHOW_VENDA_MEDIA_DIARIA && (
                    <Td {...centerCol}>
                      {formatNumber(item.vendaMediaDiaria, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Td>
                  )}
                  <Td {...centerCol}>
                    {formatNumber(item.coberturaDisponivel)}
                  </Td>
                  <Td {...centerCol}>
                    {item.quantidadeRecomendada.toLocaleString('pt-BR')}
                  </Td>
                  <Td {...centerCol}>
                    {/* Badge abaixo do valor para manter os números alinhados */}
                    <VStack spacing={1}>
                      <Text>
                        {item.quantidadeDesejada.toLocaleString('pt-BR')}
                      </Text>

                      {item.quantidadeDesejadaAlterada && (
                        <Badge colorScheme="orange" fontSize="2xs">
                          Alterado
                        </Badge>
                      )}
                    </VStack>
                  </Td>
                  <Td {...centerCol}>
                    {item.itemSemRecomendacao ? (
                      <Badge colorScheme="purple" variant="subtle">
                        {formatLotes(item.quantidadeLotes, item.loteMultiplo)}
                      </Badge>
                    ) : (
                      <Badge
                        color={getAlteracaoColor(item.percentualAlteracao)}
                        variant="subtle"
                      >
                        {item.percentualAlteracao > 0 ? '+' : ''}
                        {item.percentualAlteracao.toFixed(1)}%
                      </Badge>
                    )}
                  </Td>
                  <Td {...centerCol}>
                    <Badge variant="outline" color="black">
                      {item.tipoPedido}
                    </Badge>
                  </Td>
                  <Td {...centerCol}>{formatDesconto(item.desconto)}</Td>
                  <Td minW="200px">
                    {item.justificativaSolicitante
                      ? item.justificativaSolicitante
                      : ''}
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  )
}

export default GroupTable
